import { useState, type FormEvent } from 'react';
import { Send, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';
import SEO from '@/components/common/SEO';
import ObangsaekStripe from '@/components/editorial/ObangsaekStripe';

/**
 * Contact page.
 *
 * The team address is deliberately NOT shown and NOT present in this bundle —
 * it lives only in the Worker route (apps/api/src/routes/contact.ts). A mailto:
 * on a public page is harvested within days, and inbound spam is the last thing
 * a site rebuilding its quality signals needs. The form is the whole surface.
 *
 * Prerendered and indexable: a reachable contact route is a baseline trust
 * signal for AdSense review, so it carries a real title, description and
 * canonical rather than falling through to the SPA shell.
 */

const API_URL = import.meta.env.VITE_API_URL || '';

type Status = { kind: 'idle' | 'sending' } | { kind: 'sent' } | { kind: 'error'; message: string };

const FIELD =
  'w-full rounded-lg border border-ink-200 bg-white px-3 py-2.5 text-sm text-ink-900 ' +
  'placeholder:text-ink-400 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-cheong-500 ' +
  'dark:border-surface-dark-border dark:bg-surface-dark-card dark:text-ink-50';

const LABEL = 'mb-1.5 block text-sm font-medium text-ink-900 dark:text-ink-50';

export default function Contact() {
  const [status, setStatus] = useState<Status>({ kind: 'idle' });

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (status.kind === 'sending') return;

    const form = event.currentTarget;
    const data = Object.fromEntries(new FormData(form).entries());
    setStatus({ kind: 'sending' });

    try {
      const res = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      const json = (await res.json().catch(() => ({}))) as { error?: string };

      if (!res.ok) {
        setStatus({ kind: 'error', message: json.error ?? 'Something went wrong. Please try again.' });
        return;
      }
      form.reset();
      setStatus({ kind: 'sent' });
    } catch {
      setStatus({
        kind: 'error',
        message: 'Could not reach the server. Check your connection and try again.',
      });
    }
  }

  return (
    <div className="bg-white dark:bg-surface-dark">
      <SEO
        title="Contact KoreanHub"
        description="Get in touch with the KoreanHub team — questions, corrections to a guide, partnership enquiries, or press."
        path="/contact"
      />

      {/* Header */}
      <header className="border-b border-ink-200 bg-ink-50 dark:border-surface-dark-border dark:bg-white/[0.02]">
        <div className="mx-auto max-w-article px-4 py-10 sm:px-6">
          <h1 className="text-3xl font-bold leading-tight text-ink-900 dark:text-ink-50 sm:text-4xl">
            Contact KoreanHub
          </h1>
          <p
            lang="ko"
            className="mt-2 font-korean text-lg font-medium text-ink-500 dark:text-ink-400"
          >
            문의하기
          </p>
          <p className="mt-4 max-w-xl text-base leading-7 text-ink-700 dark:text-ink-200">
            Questions about a guide, a correction to something we got wrong, partnership or press
            enquiries — this reaches the team directly. We read everything and reply to most
            messages within a few days.
          </p>
        </div>
      </header>

      <ObangsaekStripe />

      <div className="mx-auto max-w-article px-4 py-10 sm:px-6">
        {/* Card surface: Baek warm white per DESIGN.md */}
        <div className="overflow-hidden rounded-xl border border-ink-200 bg-ink-50 shadow-sm dark:border-surface-dark-border dark:bg-surface-dark-card">
          <div className="h-1 w-full bg-jeok-500 dark:bg-jeok-dark" />

          <form onSubmit={handleSubmit} className="space-y-5 p-5 sm:p-6" noValidate>
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              <div>
                <label htmlFor="name" className={LABEL}>
                  Name <span lang="ko" className="font-korean text-ink-400">이름</span>
                </label>
                <input id="name" name="name" type="text" required maxLength={120}
                  autoComplete="name" className={FIELD} placeholder="Your name" />
              </div>
              <div>
                <label htmlFor="email" className={LABEL}>
                  Email <span lang="ko" className="font-korean text-ink-400">이메일</span>
                </label>
                <input id="email" name="email" type="email" required maxLength={254}
                  autoComplete="email" className={FIELD} placeholder="you@example.com" />
              </div>
            </div>

            <div>
              <label htmlFor="subject" className={LABEL}>
                Subject <span lang="ko" className="font-korean text-ink-400">제목</span>
              </label>
              <input id="subject" name="subject" type="text" required maxLength={200}
                className={FIELD} placeholder="What is this about?" />
            </div>

            <div>
              <label htmlFor="message" className={LABEL}>
                Message <span lang="ko" className="font-korean text-ink-400">내용</span>
              </label>
              <textarea id="message" name="message" required maxLength={5000} rows={7}
                className={`${FIELD} resize-y`}
                placeholder="Tell us what you need. If it's about a specific guide, mentioning which one helps." />
            </div>

            {/*
              Honeypot. Hidden from people and from screen readers; bots fill it
              in and the server silently drops the submission.
            */}
            <div className="hidden" aria-hidden="true">
              <label htmlFor="website">Leave this field empty</label>
              <input id="website" name="website" type="text" tabIndex={-1} autoComplete="off" />
            </div>

            <div className="flex flex-wrap items-center gap-4 pt-1">
              <button
                type="submit"
                disabled={status.kind === 'sending'}
                className="inline-flex items-center gap-2 rounded-lg bg-jeok-500 px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-[#A00D25] disabled:cursor-not-allowed disabled:opacity-60 dark:bg-jeok-dark"
              >
                {status.kind === 'sending' ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                    Sending…
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4" aria-hidden="true" />
                    Send message
                  </>
                )}
              </button>

              <p aria-live="polite" className="text-sm">
                {status.kind === 'sent' && (
                  <span className="inline-flex items-center gap-1.5 font-medium text-green-700 dark:text-green-500">
                    <CheckCircle2 className="h-4 w-4" aria-hidden="true" />
                    Thanks — your message is on its way.
                  </span>
                )}
                {status.kind === 'error' && (
                  <span className="inline-flex items-center gap-1.5 font-medium text-jeok-500 dark:text-jeok-dark">
                    <AlertCircle className="h-4 w-4" aria-hidden="true" />
                    {status.message}
                  </span>
                )}
              </p>
            </div>
          </form>
        </div>

        <p className="mt-4 text-xs leading-relaxed text-ink-500 dark:text-ink-400">
          We use what you send only to reply to you. See the{' '}
          <a
            href="/privacy"
            className="font-medium text-cheong-500 underline dark:text-cheong-dark"
          >
            privacy policy
          </a>
          .
        </p>
      </div>
    </div>
  );
}
